import { Thead } from "@client/components/round/matrix/DisagreementsMatrix";
import { MatrixTable as Table } from "@client/components/round/matrix/common";
import { Tooltip } from "@client/components/utils/Tooltip";
import { StateContext } from "@client/pages/round/Round";
import { formatMoney } from "@client/utils/formatMoney";
import { useTheme } from "@client/utils/useTheme";
import { TinyColor } from "@ctrl/tinycolor";
import { roundReducer, UnconfirmedActionMessage } from "@engine/core";
import {
  assertGetFunder,
  getEvaluableRequestBase,
  getEvaluatorActiveEvaluationIds,
} from "@engine/round/accessors";
import { Funder, Proposal, RoundAggregate } from "@engine/round/aggregate";
import { trancheListFromIncrementalEvalSteps } from "@engine/round/incrementalEvalsUtils";
import {
  getRecommenderLayerInput,
  RecommenderLayerInput,
} from "@lib/algoInterface/runSProcessOnRound";
import { trancheListToAlgoTranches } from "@lib/algoInterface/trancheListToAlgoInterface";
import { deepCopy } from "@lib/deepCopy";
import { Tranche } from "@lib/tranche";
import { Evaluator } from "@lib/types";
import { generateActionId } from "@lib/utils/idGeneration";
import Matrix from "algorithm/matrix";
import { multiLayerSProcess } from "algorithm/multiLayerSProcess";
import Vector from "algorithm/vector";
import clsx from "clsx";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useParams, useSearchParams } from "wouter";
import { EditableNumberField } from "../TrancheListEditor/EditableNumberField";

type FunderMarginalAllocations = {
  [recommenderId: string]: {
    [evaluableId: string]: {
      marginalAllocation: number;
    };
  };
};

type FunderAllocations = {
  [recommenderId: string]: {
    [proposalId: string]: {
      amount: number;
    };
  };
};

// See mk_comment in sim.py
type IncreaseComment =
  | { type: "largeIncrease"; explanation: string; value: string }
  | { type: "relativeIncrease"; explanation: string; value: string }
  | null;

type Allocation = {
  marginalAllocation: number;
  increaseComment: IncreaseComment;
  rating: number | null;
  isHighlighted: boolean;
};

type RowType = {
  proposal: Proposal;
  proposalTotal: number;
  averageAmount: number;
  averageScore: number;
  averageNewAlloc: number;
  allocations: {
    [evaluatorId: string]: Allocation;
  };
};

const cellClassName =
  "border-b border-r border-solid z-1 relative text-center group-hover:bg-stone-100! dark:group-hover:bg-black!";

type InterfaceData = {
  rows: RowType[];
  recommenders: Evaluator[];
  recommenderTotals: { [recommenderId: string]: number };
  recommenderMaxMarginalAllocation: { [recommenderId: string]: number };
  recommenderWeightedAverageScores: { [recommenderId: string]: number };
};

const formatAsPercentage = (x: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(x);
};

export function IncrementalEvals(_: { funder: Funder }) {
  return "Disabled until saving works again";
}

export function FormerIncrementalEvals({
  funder: contextFunder,
}: {
  funder: Funder;
}) {
  const {
    round: contextRound,
    underlyingUserId,
    actingAsUserId,
  } = useContext(StateContext);
  const [round, setRound] = useState(contextRound);
  const { isDarkMode } = useTheme();
  const [zenMode, setZenMode] = useState(getZenModeFromLocalStorage());

  const formatMoneyOptions = {
    showMagnitudeUnit: !zenMode,
    showDollarSign: !zenMode,
    showCommas: !zenMode,
  };

  let funder = deepCopy(assertGetFunder(round, contextFunder.id));

  // the funder tranche lists could be set some other way, like via the MVF
  // graph, we reset them here.
  funder.trancheList = trancheListFromIncrementalEvalSteps(
    funder,
    round.holdProposalId,
  );

  const [incrementAmount, setIncrementAmount] = useState(
    getIncrementAmountFromLocalStorage(),
  );

  const prevHistix = useRef<number | undefined>(undefined);

  const incrementTriggeredRef = useRef(false);
  const [, navigate] = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const uncheckedHistix = searchParams.get("histix");
  const histix = uncheckedHistix ? Number(uncheckedHistix) : undefined;

  useEffect(() => {
    // the page has been reloaded and there was a histix in the url
    //   histix !== undefined && prevHistix.current === undefined
    // the url has been edited
    //   histix == undefined && prevHistix.current !== undefined
    // the page has just been navigated to
    //   histix == undefined && prevHistix.current == undefined
    // The above statements reduce to:
    if (histix === undefined || prevHistix.current === undefined) {
      // in all cases the histix in the URL is in an undefined state, so we
      // "reset" the history of the page

      incrementTriggeredRef.current = true;
      const histix = funder.incrementalEvalIndex;
      prevHistix.current = histix;

      // for (let i = 0; i <= funder.incrementalEvalIndex; i++) {
      //   if (i === 0) {
      //     routes
      //       .allocationsAggregator({
      //         ...route.params,
      //         histix: i,
      //       })
      //       .replace();
      //   } else {
      //     routes
      //       .allocationsAggregator({
      //         ...route.params,
      //         histix: i,
      //       })
      //       .push();
      //   }
      // }

      return;
    }

    if (!incrementTriggeredRef.current) {
      if (histix > prevHistix.current) {
        redo();
      } else if (histix < prevHistix.current) {
        undo();
      }
    } else {
      incrementTriggeredRef.current = false;
    }

    prevHistix.current = histix;
    // TODO(joel): We're warned that funder.incrementalEvalIndex, route.params, redo, and undo are missing dependencies. Jason strongly warned me against messing with dependency lists so I'm leaving it for now but we should perhaps investigate.
  }, [histix]); // eslint-disable-line react-hooks/exhaustive-deps

  function performAction(msg: UnconfirmedActionMessage) {
    try {
      const result = roundReducer.performAction(round, msg);
      if (result instanceof Error) {
        throw new Error(
          `Failed to apply event ${msg.payload.type}: ${result.message}`,
        );
      }
      setRound(result);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  function undo() {
    const action: UnconfirmedActionMessage = {
      id: generateActionId(),
      actingAsUserId,
      underlyingUserId,
      aggregateId: round.id,
      payload: {
        type: "INCREMENTAL_EVALS_UNDO",
        evaluatorId: funder.id,
      },
      undoes: null,
      redoes: null,
    };

    performAction(action);
  }

  function redo() {
    const action: UnconfirmedActionMessage = {
      id: generateActionId(),
      actingAsUserId,
      underlyingUserId,
      aggregateId: round.id,
      payload: {
        type: "INCREMENTAL_EVALS_REDO",
        evaluatorId: funder.id,
      },
      undoes: null,
      redoes: null,
    };

    performAction(action);
  }

  function incrementOrDecrementRecommender(
    recommenderId: string,
    incrementMult: number,
  ) {
    incrementTriggeredRef.current = true;
    const action: UnconfirmedActionMessage = {
      id: generateActionId(),
      actingAsUserId,
      underlyingUserId,
      payload: {
        type: "INCREMENTAL_EVALS_PUSH",
        evaluatorId: funder.id,
        evaluableId: recommenderId,
        incrementAmount: incrementAmount * incrementMult,
      },
      undoes: null,
      redoes: null,
      aggregateId: round.id,
    };
    navigate(
      `/round/${round.id}/allocations/${params ? params.node : "aggregator"}/incremental-evals?histix=${funder.incrementalEvalIndex + 1}`,
    );

    incrementTriggeredRef.current = true;
    performAction(action);
  }

  const className =
    "border-b border-r border-solid z-1 relative min-w-[80px] bg-white dark:bg-[#3a3a3a] border-gray-300 dark:border-[#181818]";

  const recommenderLayerInput = useMemo(
    () => getRecommenderLayerInput(round),
    // TODO(joel): We're warned that round and getProposalForm are missing dependencies. Jason strongly warned me against messing with dependency lists so I'm leaving it for now but we should perhaps investigate.
    // Existing comment: round and getProposalForm will change on most renders
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const data = getIncrementalEvalsData(
    recommenderLayerInput,
    funder,
    incrementAmount,
    round,
  );

  const recommenderTotals = Object.values(data.recommenderTotals).reduce(
    (acc, cur) => acc + cur,
    0,
  );

  const filteredRows = data.rows.filter((row) =>
    Object.values(row.allocations).some(
      (alloc) => alloc.marginalAllocation > 0,
    ),
  );

  // sort:
  // 1. Highlighted rows first (no highlights last)
  // 2. (cur_alloc + exp_alloc) descending
  // 3. proposal name ascending
  const sortFn = (a: RowType, b: RowType) => {
    const aHasHighlight = Object.values(a.allocations).some(
      (alloc) => alloc.isHighlighted,
    );
    const bHasHighlight = Object.values(b.allocations).some(
      (alloc) => alloc.isHighlighted,
    );

    if (aHasHighlight !== bHasHighlight) {
      return aHasHighlight ? -1 : 1;
    }

    const aScore = a.proposalTotal + a.averageNewAlloc;
    const bScore = b.proposalTotal + b.averageNewAlloc;
    if (aScore !== bScore) {
      return bScore - aScore;
    }

    return a.proposal.shortname.localeCompare(b.proposal.shortname);
  };

  const highlightedRows = filteredRows
    .filter((row) =>
      Object.values(row.allocations).some((alloc) => alloc.isHighlighted),
    )
    .sort(sortFn);
  const nonHighlightedRows = filteredRows
    .filter(
      (row) =>
        !Object.values(row.allocations).some((alloc) => alloc.isHighlighted),
    )
    .sort(sortFn);

  const sortedRows = [...highlightedRows, ...nonHighlightedRows];

  const handleIncrementAmountChange = (value: number) => {
    setIncrementAmount(value);
    setIncrementAmountToLocalStorage(value);
  };

  const handleZenModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZenMode(e.target.checked);
    setZenModeToLocalStorage(e.target.checked);
  };

  return (
    <>
      <div className="pl-2 py-2 flex relative justify-between items-center">
        <div className="flex flex-col gap-2">
          <div>
            <Tooltip
              title={`${zenMode ? "Show" : "Hide"} average columns and $, k, and comma characters`}
              placement="right-start"
            >
              <label className="cursor-pointer">
                <input
                  type="checkbox"
                  checked={zenMode}
                  onChange={handleZenModeChange}
                  className="mr-1"
                />
                Zen mode
              </label>
            </Tooltip>
          </div>
          <div className="flex gap-2">
            <div>Total allocated:</div>
            <div>{formatMoney(recommenderTotals)}</div>
          </div>
        </div>
        <div className="h-full flex flex-col justify-end">
          <div className="flex gap-2">
            <div>Increment amount:</div>
            <EditableNumberField
              value={incrementAmount}
              onSubmitted={handleIncrementAmountChange}
              isMoney={true}
              min={0}
              max={1.0e7}
              className="max-w-20"
            />
          </div>
        </div>
      </div>
      <div className="overflow-auto ml-2">
        <Table>
          <Thead highZIndex>
            <tr>
              <th className="sticky left-0 z-30 border-b border-r border-solid border-gray-300 dark:border-[#181818]">
                <div className="flex justify-between p-1">
                  <div className="min-w-40 text-left">Org Name</div>
                  <div className="flex divide-x">
                    {!zenMode && (
                      <>
                        {/* <div className="min-w-14">Score Avg.</div> */}
                        <div className="min-w-14">Marg. Avg.</div>
                      </>
                    )}
                    <div className="min-w-14">Total</div>
                  </div>
                </div>
              </th>
              {data.recommenders.map((recommender) => {
                const weightedAverageScore =
                  data.recommenderWeightedAverageScores[recommender.id];
                return (
                  <th
                    className="relative border-b border-solid cursor-pointer select-none border-gray-300 dark:border-[#181818]"
                    key={recommender.id}
                    onClick={(ev) => {
                      if (ev.button === 0 && !ev.shiftKey) {
                        incrementOrDecrementRecommender(recommender.id, 1);
                      } else {
                        incrementOrDecrementRecommender(recommender.id, -1);
                      }
                    }}
                  >
                    <div>{recommender.shortname}</div>
                    <div className="px-[14px]">
                      {formatMoney(
                        data.recommenderTotals[recommender.id],
                        formatMoneyOptions,
                      )}
                    </div>
                    <div className="absolute bottom-[2px] left-1 text-[9px] text-red-700">
                      <Tooltip
                        title="Weighted sum of scores (average rating weighted by allocation size)"
                        placement="right-start"
                      >
                        {weightedAverageScore?.toFixed(1)}
                      </Tooltip>
                    </div>
                  </th>
                );
              })}
            </tr>
          </Thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={i} className="group">
                <th className="p-1 min-w-[48px] sticky left-0 z-10 border-b border-r border-solid group-hover:bg-stone-100! dark:group-hover:bg-black! border-gray-300 dark:border-[#181818]">
                  <div className="flex justify-between">
                    <span className="w-40 text-left break-words">
                      {row.proposal.shortname}
                    </span>
                    <span>
                      <div className="flex">
                        {!zenMode && (
                          <>
                            {/* <div className="min-w-14">
                              {row.averageScore.toFixed(1)}
                            </div> */}
                            <div className="min-w-14">
                              {formatMoney(row.averageAmount)}
                            </div>
                          </>
                        )}
                        <div className="min-w-14">
                          {formatMoney(row.proposalTotal, formatMoneyOptions)}
                        </div>
                      </div>
                    </span>
                  </div>
                </th>
                {data.recommenders.map((recommender, i) => {
                  const maxValue =
                    data.recommenderMaxMarginalAllocation[recommender.id];

                  let normalizedDifferenceFromMax;
                  if (maxValue === 0) {
                    normalizedDifferenceFromMax = 1;
                  } else {
                    normalizedDifferenceFromMax =
                      (maxValue -
                        row.allocations[recommender.id].marginalAllocation) /
                      maxValue;
                  }

                  const cell = row.allocations[recommender.id];

                  // only show the cell if there is a non-zero marginal allocation
                  if (Math.abs(cell.marginalAllocation) < 50) {
                    return (
                      <td
                        key={i}
                        className={clsx(
                          className,
                          cellClassName,
                          "bg-white dark:bg-[#3a3a3a]",
                        )}
                      />
                    );
                  }

                  let alpha = Math.min(1 - normalizedDifferenceFromMax, 2) / 2;
                  if (alpha < 0) alpha = 0;

                  let backgroundColor = new TinyColor("green").setAlpha(alpha);
                  backgroundColor = isDarkMode
                    ? backgroundColor
                    : backgroundColor.lighten(25);

                  let highlightedCellColor = new TinyColor("#d97706");
                  highlightedCellColor = isDarkMode
                    ? highlightedCellColor
                    : highlightedCellColor.lighten(25);

                  const marginalAllocationDisplay =
                    zenMode && cell.marginalAllocation < 1000
                      ? "<1"
                      : formatMoney(
                          cell.marginalAllocation,
                          formatMoneyOptions,
                        );

                  return (
                    <td
                      key={i}
                      className={clsx(cellClassName, className)}
                      style={{
                        backgroundColor: cell.isHighlighted
                          ? highlightedCellColor.toRgbString()
                          : backgroundColor.toRgbString(),
                      }}
                    >
                      <div className="absolute top-[2px] left-1 text-[9px] text-red-700">
                        {cell.rating}
                      </div>
                      {marginalAllocationDisplay}
                      {cell.increaseComment && (
                        <div className="absolute bottom-[2px] right-1">
                          <Tooltip
                            title={cell.increaseComment.explanation}
                            placement="right-start"
                          >
                            <div className=" text-[9px] text-stone-600 dark:text-stone-300">
                              {cell.increaseComment.type === "largeIncrease"
                                ? cell.increaseComment.value
                                : cell.increaseComment.value + "x"}
                            </div>
                          </Tooltip>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );
}

function getIncrementalEvalsData(
  recommenderLayerInput: RecommenderLayerInput,
  funder: Funder,
  incrementAmount: number,
  round: RoundAggregate,
): InterfaceData {
  const activeEvaluationIds = new Set(getEvaluatorActiveEvaluationIds(funder));
  const currentAllocations = getFunderAllocations(
    funder.trancheList,
    recommenderLayerInput,
    activeEvaluationIds,
  );

  const currentAllocsPerProposal: { [proposalId: string]: number } = {};
  const currentAllocsPerRecommender: { [recommenderId: string]: number } = {};
  for (const [recommenderId, recommenderAllocations] of Object.entries(
    currentAllocations,
  )) {
    if (!currentAllocsPerRecommender[recommenderId]) {
      currentAllocsPerRecommender[recommenderId] = 0;
    }

    for (const [proposalId, allocation] of Object.entries(
      recommenderAllocations,
    )) {
      if (!currentAllocsPerProposal[proposalId]) {
        currentAllocsPerProposal[proposalId] = 0;
      }

      currentAllocsPerRecommender[recommenderId] += allocation.amount;
      currentAllocsPerProposal[proposalId] += allocation.amount;
    }
  }

  const marginalAllocations = getMarginalAllocations(
    funder.trancheList,
    recommenderLayerInput,
    currentAllocsPerProposal,
    activeEvaluationIds,
    incrementAmount,
  );

  const recommenderMaxMarginalAllocation: { [recommenderId: string]: number } =
    {};
  const recommenderWeightedScores: { [recommenderId: string]: number } = {};
  const recommenderTotalMarginalAllocations: {
    [recommenderId: string]: number;
  } = {};

  const rows: InterfaceData["rows"] = [];
  for (const proposal of recommenderLayerInput.orderedProposals) {
    const row: InterfaceData["rows"][0] = {
      proposal,
      proposalTotal: currentAllocsPerProposal[proposal.id] ?? 0,
      averageAmount: 0,
      allocations: {},
      averageScore: 0,
      averageNewAlloc: 0,
    };
    const baseAmount = getEvaluableRequestBase(round, proposal.id);

    let totalNewAlloc = 0;
    let marginalTotal = 0;
    let scoresTotal = 0;
    let numNonemptyScores = 0;
    for (const recommender of recommenderLayerInput.orderedRecommenders) {
      const currentAllocation = currentAllocsPerProposal[proposal.id] ?? 0;
      let marginalAllocation =
        marginalAllocations[recommender.id]?.[proposal.id]
          ?.marginalAllocation ?? 0;

      // Show a rating in the cell iff there was a marginal allocation
      // (the display logic displays an empty cell on a null rating)
      const rating =
        marginalAllocation === 0
          ? null
          : (funder.scores[recommender.id]?.[proposal.id] ?? null);

      let increaseComment: IncreaseComment = null;

      if (baseAmount && marginalAllocation > baseAmount / 4) {
        increaseComment = {
          type: "largeIncrease",
          explanation: `Marginal allocation (${Math.round(marginalAllocation)}) > base amount / 4 ${Math.round(baseAmount / 4)}`,
          value: formatAsPercentage(marginalAllocation / baseAmount),
        };
      } else if (
        currentAllocation &&
        currentAllocation + marginalAllocation > currentAllocation * 1.3
      ) {
        increaseComment = {
          type: "relativeIncrease",
          explanation: `New allocation (${Math.round(currentAllocation + marginalAllocation)}) > current allocation * 1.3 (${Math.round(currentAllocation * 1.3)})`,
          value: (1 + marginalAllocation / currentAllocation).toFixed(1),
        };
      }

      const isHighlighted =
        marginalAllocation + currentAllocation > currentAllocation * 2;

      row.allocations[recommender.id] = {
        marginalAllocation,
        increaseComment,
        rating,
        isHighlighted,
      };

      const score = funder.scores[recommender.id]?.[proposal.id] ?? 0;
      if (typeof score !== "undefined") {
        scoresTotal += score;
        numNonemptyScores++;
      }

      recommenderMaxMarginalAllocation[recommender.id] = Math.max(
        recommenderMaxMarginalAllocation[recommender.id] || 0,
        marginalAllocation,
      );

      if (marginalAllocation > 0) {
        recommenderWeightedScores[recommender.id] =
          (recommenderWeightedScores[recommender.id] || 0) +
          score * marginalAllocation;
        recommenderTotalMarginalAllocations[recommender.id] =
          (recommenderTotalMarginalAllocations[recommender.id] || 0) +
          marginalAllocation;
      }

      marginalTotal += marginalAllocation;
      totalNewAlloc += marginalAllocation + currentAllocation;
    }

    row.averageAmount =
      marginalTotal / recommenderLayerInput.orderedRecommenders.length;

    row.averageScore = scoresTotal / numNonemptyScores;

    row.averageNewAlloc =
      totalNewAlloc / recommenderLayerInput.orderedRecommenders.length;

    rows.push(row);
  }

  const recommenderWeightedAverageScores: { [recommenderId: string]: number } =
    {};
  for (const recommender of recommenderLayerInput.orderedRecommenders) {
    const totalMarginalAllocation =
      recommenderTotalMarginalAllocations[recommender.id] || 0;
    const weightedScoreSum = recommenderWeightedScores[recommender.id] || 0;
    const weightedAverageScore =
      totalMarginalAllocation > 0
        ? weightedScoreSum / totalMarginalAllocation
        : 0;
    recommenderWeightedAverageScores[recommender.id] = weightedAverageScore;
  }

  // SP-375: Filtering out users who don't have MVFs
  const filteredRecommenders = recommenderLayerInput.orderedRecommenders.filter(
    (recommender) =>
      Object.values(recommender.evaluations).length > 1 ||
      recommender.shortname.toLowerCase().includes("mean"),
  );

  return {
    rows,
    recommenders: filteredRecommenders,
    recommenderTotals: currentAllocsPerRecommender,
    recommenderMaxMarginalAllocation,
    recommenderWeightedAverageScores,
  };
}

function getMarginalAllocations(
  funderTrancheList: Tranche[],
  recommenderLayerInput: RecommenderLayerInput,
  currentAllocations: { [proposalId: string]: number },
  activeEvaluationIds: Set<string>,
  incrementAmount: number,
): FunderMarginalAllocations {
  const marginalAllocations: FunderMarginalAllocations = {};

  delete currentAllocations[recommenderLayerInput.holdProposal.id];

  for (const recommender of recommenderLayerInput.orderedRecommenders) {
    const nextFunderTrancheList = addTrancheWithAllocation(
      funderTrancheList,
      recommender.id,
      incrementAmount,
    );

    const nextAllocations = getFunderAllocations(
      nextFunderTrancheList,
      recommenderLayerInput,
      activeEvaluationIds,
    );

    const nextAllocsPerProposal: { [proposalId: string]: number } = {};
    for (const recommenderAllocations of Object.values(nextAllocations)) {
      for (const [proposalId, allocation] of Object.entries(
        recommenderAllocations,
      )) {
        if (!nextAllocsPerProposal[proposalId]) {
          nextAllocsPerProposal[proposalId] = 0;
        }

        nextAllocsPerProposal[proposalId] += allocation.amount;
      }
    }

    marginalAllocations[recommender.id] = {};
    // if the proposalId is in the next allocations, than it must be in the
    // current allocations as well since the SProcess is increasing everywhere
    // and nextAllocations is strictly larger than currentAllocations (as long
    // as incerementAmount > 0)
    for (const proposalId of Object.keys(nextAllocations[recommender.id])) {
      const currentAllocation = currentAllocations[proposalId] ?? 0;
      const nextAllocation = nextAllocsPerProposal[proposalId] ?? 0;

      const marginalAllocation = nextAllocation - currentAllocation;

      marginalAllocations[recommender.id][proposalId] = { marginalAllocation };
    }
  }

  return marginalAllocations;
}

function getFunderAllocations(
  funderTrancheList: Tranche[],
  recommenderLayerInput: RecommenderLayerInput,
  activeEvaluationIds: Set<string>,
): FunderAllocations {
  const funderPreferenceList = trancheListToAlgoTranches(
    funderTrancheList,
    recommenderLayerInput.recommenderIndicesMap,
    {},
    activeEvaluationIds,
  );

  const lastTranche = funderPreferenceList.getRow(
    funderPreferenceList.n_rows - 1,
  );
  const funderBudget = lastTranche.sum();

  const layerPreferenceLists: [
    Matrix<"tranche", "recommender+hold">[],
    Matrix<"tranche", "proposal+hold">[],
  ] = [
    [
      funderPreferenceList.rename(
        "tranche -> tranche",
        "evaluable+hold -> recommender+hold",
      ),
    ],
    recommenderLayerInput.recommenderPreferenceLists,
  ];

  const { layerAccounts } = multiLayerSProcess(
    new Vector([funderBudget]),
    layerPreferenceLists,
  );
  const recommenderAccounts = layerAccounts[1];

  const funderAllocations: FunderAllocations = {};
  for (const [recommenderId, recommenderIndex] of Object.entries(
    recommenderLayerInput.recommenderIndicesMap,
  )) {
    for (const [proposalId, proposalIndex] of Object.entries(
      recommenderLayerInput.proposalIndicesMap,
    )) {
      const allocation =
        recommenderAccounts[proposalIndex][recommenderIndex - 1];
      if (!funderAllocations[recommenderId]) {
        funderAllocations[recommenderId] = {};
      }

      funderAllocations[recommenderId][proposalId] = { amount: allocation };
    }
  }

  delete funderAllocations[recommenderLayerInput.holdProposal.id];

  return funderAllocations;
}

function addTrancheWithAllocation(
  trancheList: Tranche[],
  evaluableId: string,
  amount: number,
): Tranche[] {
  const newTranche: Tranche = {
    height: 0.01,
    allocations: {
      [evaluableId]: {
        amount: amount,
        bottomPlaced: true,
      },
    },
  };

  return [...trancheList, newTranche];
}

function getIncrementAmountFromLocalStorage(): number {
  const storedValue = localStorage.getItem("incrementAmount");
  return storedValue !== null ? Number(storedValue) : 200000;
}

function setIncrementAmountToLocalStorage(value: number) {
  localStorage.setItem("incrementAmount", value.toString());
}

function getZenModeFromLocalStorage(): boolean {
  const storedValue = localStorage.getItem("zenMode");
  return storedValue !== null ? JSON.parse(storedValue) : false;
}

function setZenModeToLocalStorage(value: boolean) {
  localStorage.setItem("zenMode", value.toString());
}
